<template>
    <div class="relative">
        <div class="fixed inset-0 z-[999]" v-if="showOnMap">
            <Suspense>
                <MapResults />
            </Suspense>
        </div>
        <div
            class="w-full h-full flex-col justify-start items-start flex @container relative"
        >
            <!-- FAVORITES HEADER -->
            <h1
                class="flex-col justify-start items-start flex text-slate-800 text-2xl font-semibold leading-loose"
            >
                Favorite Hotels
            </h1>
            <!-- END FAVORITES HEADER -->
            <!-- START FAVORITES ACTION BAR -->
            <FavoritesActionBar />
            <!-- END FAVORITES ACTION BAR -->
            <!-- START FAVORITES RESULTS -->
            <section
                v-for="(properties, group, index) in usersFavHotels"
                :key="group"
                :ref="(el) => setGroupRef(el, index)"
                class="w-full -z-10 scroll-mt-12"
                v-if="!loading && !noResultsFound"
            >
                <!-- start group header -->
                <div
                    class="w-full pt-8 justify-between items-start gap-2.5 flex"
                    v-if="groupFavsBy && !noResultsFound"
                >
                    <div class="text-slate-800 text-lg font-medium leading-7">
                        {{ $filters.ucWords(group) }}
                    </div>
                    <button
                        v-if="index !== totalGroups - 1"
                        @click="() => scrollToGroup(index)"
                        class="flex justify-center absolute right-0 rounded-full bg-white w-8 h-8 p-2 text-slate-800 items-center max-sm:focus:ring-2 max-sm:focus:ring-slate-200 focus:outline-none active:outline-none hover:text-slate-500"
                    >
                        <Icon icon="chevron-down" />
                    </button>
                </div>
                <!-- end group header -->
                <transition-group
                    class="w-full pt-4 justify-between items-start grid grid-cols-1 @md:grid-cols-2 @2xl:grid-cols-3 @3xl:grid-cols-4 gap-x-6 gap-y-8"
                    name="properties"
                    tag="div"
                >
                    <PropertyCardSmall
                        v-for="property in properties"
                        :widgetView="false"
                        :key="property.id"
                        :property="property"
                        :class="property"
                        cardType="archive"
                    />
                </transition-group>
            </section>
            <!-- END FAVORITES RESULTS -->
            <!-- OPEN MAP BUTTON (MOBILE VIEW ONLY) -->
            <div
                v-if="!loading && !noResultsFound"
                class="w-full sticky bottom-4 inset-x-0 flex justify-center sm:hidden"
            >
                <FavoritesActionBarButton
                    :customClickEvent="true"
                    @buttonClicked="openFavsMap"
                >
                    <template #icon-left>
                        <i class="fa-regular fa-map fa-sm"></i>
                    </template>
                    <template #button-text>
                        {{ showOnMap ? 'Hide map' : 'Open Map View' }}
                    </template>
                </FavoritesActionBarButton>
                <button
                    @click="() => scrollToTop()"
                    class="flex justify-center absolute right-0 text-slate-800 rounded-full bg-white w-8 h-8 p-2 items-center max-sm:focus:ring-2 max-sm:focus:ring-slate-300 focus:outline-none active:outline-none focus:border-slate-300 hover:text-slate-500"
                >
                    <Icon icon="chevron-up" />
                </button>
            </div>
            <!-- START EMPTY STATE -->
            <section
                v-if="!loading && noResultsFound"
                class="w-full flex items-center justify-center"
            >
                <EmptyState
                    class="md:rounded-lg mb-5"
                    image-name="search.svg"
                    :title="emptyStateTitle"
                    :description="emptyStateDescription"
                    :hasAction="false"
                    v-cloak
                >
                    <template #image>
                        <div
                            class="w-16 h-16 bg-slate-200 rounded-full justify-center items-center gap-2.5 inline-flex text-slate-600"
                        >
                            <div
                                class="w-8 h-8 flex items-center justify-center"
                            >
                                <svg
                                    v-if="favSearchQuery"
                                    width="32"
                                    height="32"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g id="search">
                                        <path
                                            id="vector"
                                            fill-rule="evenodd"
                                            clip-rule="evenodd"
                                            d="M10.998 4.99951C7.68434 4.99951 4.99805 7.6858 4.99805 10.9995C4.99805 14.3132 7.68434 16.9995 10.998 16.9995C14.3118 16.9995 16.998 14.3132 16.998 10.9995C16.998 7.6858 14.3118 4.99951 10.998 4.99951ZM2.99805 10.9995C2.99805 6.58123 6.57977 2.99951 10.998 2.99951C15.4163 2.99951 18.998 6.58123 18.998 10.9995C18.998 12.8482 18.371 14.5505 17.3179 15.9052L20.7051 19.2924C21.0957 19.6829 21.0957 20.3161 20.7051 20.7066C20.3146 21.0971 19.6815 21.0971 19.2909 20.7066L15.9037 17.3194C14.549 18.3724 12.8468 18.9995 10.998 18.9995C6.57977 18.9995 2.99805 15.4178 2.99805 10.9995Z"
                                            fill="currentColor"
                                        />
                                    </g>
                                </svg>
                                <svg
                                    v-else
                                    width="32"
                                    height="32"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g id="heart">
                                        <path
                                            id="vector"
                                            fill-rule="evenodd"
                                            clip-rule="evenodd"
                                            d="M16.7343 5.59133C15.4963 5.31528 14.0265 5.63779 12.7125 6.9706C12.5246 7.16122 12.2681 7.26854 12.0004 7.26854C11.7327 7.26854 11.4762 7.16122 11.2883 6.9706C9.97431 5.63779 8.50458 5.31528 7.26649 5.59133C6.00355 5.87293 4.88742 6.79708 4.34587 8.14741C3.3302 10.68 4.25311 15.3399 12.0005 19.8486C19.7478 15.3399 20.6707 10.68 19.655 8.1474C19.1134 6.79707 17.9973 5.87292 16.7343 5.59133ZM21.5112 7.40294C23.1154 11.4028 21.0173 17.0927 12.4894 21.8725C12.1857 22.0428 11.8153 22.0428 11.5115 21.8725C2.98361 17.0927 0.885443 11.4029 2.48959 7.40295C3.26845 5.46089 4.90318 4.06917 6.83124 3.63927C8.52876 3.26077 10.3838 3.64222 12.0004 4.92553C13.617 3.64222 15.4721 3.26077 17.1696 3.63926C19.0977 4.06916 20.7324 5.46087 21.5112 7.40294Z"
                                            fill="currentColor"
                                        />
                                    </g>
                                </svg>
                            </div>
                        </div>
                    </template>
                </EmptyState>
            </section>
            <!-- END EMPTY STATE -->
        </div>
        <div
            class="absolute top-0 left-0 h-[80vh] w-full flex-col justify-start items-start flex"
            v-if="loading"
        >
            <LoadingState :loading="true" />
        </div>
    </div>
</template>

<script>
import { computed, ref, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useHotelsStore } from '@/store/hotels';
import { useRewardsAndTransferPartners } from '@/composables/useRewardsAndTransferPartners.vue';
import MapResults from '@/components/hotels/search/map-results/map-results.vue';
import PropertyCardSmall from '@/components/hotels/favorites/property-card-small/property-card-small.vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import EmptyState from '@/components/ui/empty-state/empty-state.vue';
import FavoritesActionBar from '@/components/hotels/favorites/favorites-action-bar/favorites-action-bar.vue';
import 'vue3-carousel/dist/carousel.css';
import FavoritesActionBarButton from '@/components/hotels/favorites/favorites-action-bar/favorites-action-bar-button/favorites-action-bar-button.vue';
import Icon from '@/components/ui/icon/icon.vue';

export default {
    beforeRouteEnter(to, from, next) {
        document.title = 'Favorite Hotels | My10x';
        next();
    },
    components: {
        EmptyState,
        FavoritesActionBarButton,
        Icon,
        PropertyCardSmall,
        FavoritesActionBar,
        LoadingState,
        MapResults,
    },
    props: {},
    setup() {
        const hotelsStore = useHotelsStore();
        const groupRefs = ref([]);
        const { initializePointsAndTransfersData } =
            useRewardsAndTransferPartners();
        const {
            loading,
            favSearchQuery,
            showOnMap,
            groupFavsBy,
            usersFavHotels,
        } = storeToRefs(hotelsStore);
        const { setShowOnMap } = hotelsStore;

        const noResultsFound = computed(() => {
            if (
                Array.isArray(usersFavHotels.value) &&
                usersFavHotels.value.length === 0
            ) {
                return true;
            }

            if (
                typeof usersFavHotels.value === 'object' &&
                usersFavHotels.value !== null
            ) {
                return (
                    Array.isArray(usersFavHotels.value.all) &&
                    usersFavHotels.value.all.length === 0
                );
            }
            return false;
        });

        const emptyStateTitle = computed(() =>
            favSearchQuery.value
                ? 'No matching favorites found'
                : 'No favorites yet.'
        );
        const emptyStateDescription = computed(() =>
            favSearchQuery.value
                ? 'Try changing your search parameters'
                : 'Tap the heart on any hotel to save it to your favorites.'
        );

        const totalGroups = computed(() => {
            return Object.keys(usersFavHotels.value).length;
        });

        const fetchFavs = async () => {
            await hotelsStore.getUsersFavHotels();
        };

        const scrollToGroup = (currentIndex) => {
            const nextIndex = (currentIndex + 1) % totalGroups.value;
            const groupEl = groupRefs.value[nextIndex];
            if (groupEl) {
                groupEl.scrollIntoView({ behavior: 'smooth' });
            }
        };

        const scrollToTop = () => {
            const lastIndex = totalGroups.value - 1;
            scrollToGroup(lastIndex);
        };

        const setGroupRef = (el, index) => {
            if (el) {
                groupRefs.value[index] = el;
            }
        };

        const openFavsMap = () => {
            const val = !showOnMap.value;
            setShowOnMap(val);
            if (val) {
                setMapType('favorites');
            } else {
                setMapType('search');
            }
        };

        fetchFavs();

        initializePointsAndTransfersData();

        onMounted(() => {
            groupRefs.value = new Array(totalGroups.value).fill(null);
        });

        return {
            emptyStateDescription,
            emptyStateTitle,
            groupFavsBy,
            loading,
            noResultsFound,
            scrollToGroup,
            scrollToTop,
            favSearchQuery,
            setGroupRef,
            openFavsMap,
            showOnMap,
            totalGroups,
            usersFavHotels,
        };
    },
};
</script>

<style lang="scss">
.properties-move,
.properties-enter-active {
    transition: all 2.5s ease;
}

.properties-move {
    transition: transform 2.5s ease;
}
.property {
    transition: all 500ms ease;
}

.properties-leave-to {
    opacity: 0;
    transform: translateX(30px);
}

.properties-enter-from {
    opacity: 0;
    position: relative;
    transform: translateX(-30px);
}

.properties-leave-active {
    position: absolute;
    width: 100%;
    transition:
        all 2.5s ease,
        height 0.5s ease,
        margin 0.5s ease;
}

.group-container {
    scroll-snap-type: y mandatory;
}
</style>
